import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { resetConsent } from '../utils/consent';
import AnnouncementBar from './AnnouncementBar';
import { useSiteSettings } from '../context/SiteSettingsContext';
import CartSidebar from './CartSidebar';
import LanguageSwitcher from './LanguageSwitcher';
import WhatsAppButton from './WhatsAppButton';
import ConsentBanner from './ConsentBanner';
import { Icon, ICON_DEFAULTS } from '../utils/icons';
import api from '../services/api';
import { useCatalog, tagLabel } from '../utils/catalog';
import './Layout.css';
import { DEMO_MODE } from '../utils/demo';

const SHOP_NAV_MAX = 7;

const Layout = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { getCartCount, setIsCartOpen } = useCart();
  const { isAuthenticated: isCustomerAuth } = useCustomerAuth();
  const {
    storeName, storeLogoUrl, footerLinks, themeGlobalSettings, marketingConsentRequired,
    storeEmail, storePhone, storeAddress, storeNipt, storeLegalName,
  } = useSiteSettings();
  const lang = (i18n.language || 'sq').split('-')[0];
  const { categories, facets } = useCatalog(lang);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [headerMenu, setHeaderMenu] = useState(null);
  const [footerMenu, setFooterMenu] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const locale = (i18n.language || 'sq').split('-')[0];
    setHeaderMenu(null);
    setFooterMenu(null);
    Promise.all([
      api.get('/api/navigation/header', { params: { locale } }).catch(() => ({ data: null })),
      api.get('/api/navigation/footer', { params: { locale } }).catch(() => ({ data: null })),
    ]).then(([headerResponse, footerResponse]) => {
      if (cancelled) return;
      setHeaderMenu(headerResponse.data);
      setFooterMenu(footerResponse.data);
    });
    return () => { cancelled = true; };
  }, [i18n.language]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Body scroll lock while the mobile drawer is open.
  //
  // Without this, iOS Safari rubber-band lets the page underneath scroll
  // when the user touches anywhere on the (transparent) area outside the
  // drawer — confusing, and on the previous build the drawer's hardcoded
  // top offset (64px) left a literal gap above it where the underlying
  // page was both visible AND scrollable. Locking `documentElement` and
  // `body` together is the most reliable cross-iOS-version pattern; the
  // saved `overflow` is restored on close so we don't permanently freeze
  // the page if React unmounts mid-state.
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, [mobileMenuOpen]);

  // Close on `Escape` for keyboard parity with the cart drawer.
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileMenuOpen]);

  const cartCount = getCartCount();

  // The shop row under the header: every product, then the categories, then
  // the most stocked product types, so a store with few categories still
  // gets a useful row. Built from the live catalogue, never an empty link.
  const params = new URLSearchParams(location.search);
  const onListing = location.pathname === '/products';
  const shopNav = [
    { key: 'all', label: t('nav.allProducts', 'All products'), url: '/products', active: onListing && !params.get('category') && !params.get('tags') },
    ...categories.filter((category) => category.productCount > 0).map((category) => ({
      key: `c-${category.slug}`, label: category.name, url: `/products?category=${encodeURIComponent(category.slug)}`,
      active: onListing && params.get('category') === category.slug,
    })),
  ];
  // A tag that holds exactly as many products as a category is almost
  // always the same shelf under another name, so it is left out.
  const categoryCounts = new Set(categories.map((category) => category.productCount));
  (facets?.tags || []).filter(({ count }) => count >= 2 && !categoryCounts.has(count)).forEach(({ tag }) => {
    if (shopNav.length >= SHOP_NAV_MAX) return;
    shopNav.push({ key: `t-${tag}`, label: tagLabel(t, tag), url: `/products?tags=${encodeURIComponent(tag)}`, active: onListing && params.get('tags') === tag });
  });

  const defaultHeaderItems = [
    { label: t('nav.home'), url: '/' },
    { label: t('nav.products'), url: '/products' },
    { label: t('nav.about', 'About'), url: '/about' },
    { label: t('nav.contact', 'Contact'), url: '/contact' },
  ];
  const headerItems = (headerMenu?.items || defaultHeaderItems).filter((item) => item.enabled !== false);
  // Store pages (about, contact…) sit quietly at the end of the shop row;
  // home and the catalogue are already covered by the logo and the row.
  // A home or catalogue item stays when it carries a submenu.
  const pageLinks = headerItems.filter((item) => !['/', '/products'].includes(item.url)
    || (item.children || []).some((child) => child.enabled !== false));
  const quickLinks = (footerMenu?.items || footerLinks).filter((item) => item.enabled !== false);

  const menuLink = (item, className) => /^https?:\/\//i.test(item.url) ? (
    <a href={item.url} className={className} target={item.openNewTab ? '_blank' : undefined} rel={item.openNewTab ? 'noopener noreferrer' : undefined}>{item.label}</a>
  ) : (
    <Link to={item.url} className={className}>{item.label}</Link>
  );

  // Header search: every page searches the whole catalogue on /products.
  const submitSearch = (event) => {
    event.preventDefault();
    const q = searchQuery.trim();
    setMobileSearchOpen(false);
    navigate(q ? `/products?search=${encodeURIComponent(q)}` : '/products');
  };
  const searchForm = (id) => (
    <form className="navbar-search" role="search" onSubmit={submitSearch}>
      <Icon.Search size={18} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
      <label htmlFor={id} className="visually-hidden">{t('nav.search', 'Search')}</label>
      <input
        id={id}
        type="search"
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        placeholder={t('nav.searchPlaceholder', 'Search toys, brands, ages…')}
        enterKeyHint="search"
      />
    </form>
  );

  return (
    <div className="layout">
      <a className="skip-link" href="#main-content">
        {t('nav.skipToContent', 'Skip to main content')}
      </a>
      {DEMO_MODE && <div className="store-demo-bar" role="region" aria-label={t('demo.loginTitle', 'Demo store')}>
        <span>{t('demo.banner', 'This is a demo store: nothing is sold and it resets every night.')}</span>
        <Link to="/admin/login">{t('demo.tryAdmin', 'Try the admin')} →</Link>
      </div>}
      <AnnouncementBar announcement={themeGlobalSettings?.announcement} />
      <header className="navbar">
        <div className="navbar-container">
          <button
            type="button"
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? t('nav.closeMenu', 'Close menu') : t('nav.openMenu', 'Open menu')}
            aria-expanded={mobileMenuOpen}
            aria-controls="primary-navigation"
          >
            {mobileMenuOpen ? (
              <Icon.Close size={ICON_DEFAULTS.size} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
            ) : (
              <Icon.Menu size={ICON_DEFAULTS.size} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
            )}
          </button>

          <Link to="/" className="navbar-logo" aria-label={storeName}>
            {storeLogoUrl ? (
              <img src={storeLogoUrl} alt={storeName} className="navbar-logo-img" />
            ) : (
              <>
                <span className="navbar-logo-mark" aria-hidden="true">{(storeName || 'S').trim().charAt(0).toLowerCase()}</span>
                <span className="navbar-logo-text">{storeName}</span>
              </>
            )}
          </Link>

          <div className="navbar-search-desktop">{searchForm('navbar-search')}</div>

          {/*
            Mobile drawer: the shop row, the store's pages, account and
            language. The scrim absorbs taps outside the panel.
          */}
          <div
            className={`navbar-menu-scrim ${mobileMenuOpen ? 'active' : ''}`}
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          <ul
            className={`navbar-menu ${mobileMenuOpen ? 'active' : ''}`}
            id="primary-navigation"
            role={mobileMenuOpen ? 'dialog' : undefined}
            aria-modal={mobileMenuOpen ? true : undefined}
            aria-label={t('nav.menu', 'Main menu')}
          >
            {shopNav.map((item) => (
              <li key={item.key}><Link to={item.url} className={item.active ? 'active' : ''}>{item.label}</Link></li>
            ))}
            {pageLinks.map((item, index) => {
              const children = (item.children || []).filter((child) => child.enabled !== false);
              return <li key={item.id || `${item.url}-${index}`} className={`navbar-menu-page${children.length ? ' navbar-menu-parent' : ''}`}>
                {menuLink(item, location.pathname === item.url ? 'active' : '')}
                {children.length > 0 && <ul className="navbar-submenu">{children.map((child, childIndex) => <li key={child.id || `${child.url}-${childIndex}`}>{menuLink(child, location.pathname === child.url ? 'active' : '')}</li>)}</ul>}
              </li>;
            })}
            <li className="navbar-menu-page">
              <Link to={isCustomerAuth ? '/account' : '/login'}>
                {isCustomerAuth ? t('nav.account', 'My account') : t('nav.login', 'Log in')}
              </Link>
            </li>
            <li className="mobile-lang-switcher">
              <LanguageSwitcher />
            </li>
          </ul>

          <div className="navbar-actions">
            <button
              type="button"
              className="navbar-icon-button navbar-search-toggle"
              onClick={() => setMobileSearchOpen((open) => !open)}
              aria-label={t('nav.search', 'Search')}
              aria-expanded={mobileSearchOpen}
            >
              <Icon.Search size={20} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
            </button>
            <div className="desktop-lang-switcher">
              <LanguageSwitcher />
            </div>
            <Link
              to="/wishlist"
              className="navbar-icon-button navbar-desktop-only"
              aria-label={t('nav.wishlist', 'Wishlist')}
            >
              <Icon.Wishlist size={20} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
            </Link>
            <Link
              to={isCustomerAuth ? '/account' : '/login'}
              className="navbar-icon-button navbar-desktop-only"
              aria-label={isCustomerAuth ? t('nav.account', 'My account') : t('nav.login', 'Log in')}
            >
              <Icon.Account size={20} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
            </Link>
            <button
              className="navbar-icon-button"
              onClick={() => setIsCartOpen(true)}
              aria-label={t('nav.openCart', 'Open shopping cart')}
            >
              <Icon.Cart size={20} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
              {cartCount > 0 && (
                <span className="cart-badge" aria-label={t('nav.cartCount', `${cartCount} items in cart`, { count: cartCount })}>
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
        <nav className="navbar-shop" aria-label={t('nav.shopNav', 'Shop')}>
          <ul className="navbar-shop-list">
            {shopNav.map((item) => (
              <li key={item.key}><Link to={item.url} className={item.active ? 'active' : ''} aria-current={item.active ? 'page' : undefined}>{item.label}</Link></li>
            ))}
          </ul>
          {pageLinks.length > 0 && (
            <ul className="navbar-shop-pages">
              {pageLinks.map((item, index) => {
                const children = (item.children || []).filter((child) => child.enabled !== false);
                return <li key={item.id || `${item.url}-${index}`} className={children.length ? 'navbar-menu-parent' : ''}>
                  {menuLink(item, location.pathname === item.url ? 'active' : '')}
                  {children.length > 0 && <ul className="navbar-submenu">{children.map((child, childIndex) => <li key={child.id || `${child.url}-${childIndex}`}>{menuLink(child, location.pathname === child.url ? 'active' : '')}</li>)}</ul>}
                </li>;
              })}
            </ul>
          )}
        </nav>
        {mobileSearchOpen && <div className="navbar-search-mobile">{searchForm('navbar-search-mobile')}</div>}
      </header>

      <CartSidebar />

      <main id="main-content" className="main-content" tabIndex={-1}>
        <Outlet />
      </main>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section footer-brand">
              <Link to="/" className="navbar-logo footer-logo" aria-label={storeName}>
                {storeLogoUrl ? <img src={storeLogoUrl} alt={storeName} className="navbar-logo-img" /> : <>
                  <span className="navbar-logo-mark" aria-hidden="true">{(storeName || 'S').trim().charAt(0).toLowerCase()}</span>
                  <span className="navbar-logo-text">{storeName}</span>
                </>}
              </Link>
              <ul className="footer-contact">
                {storePhone && <li><a href={`tel:${storePhone.replace(/\s+/g, '')}`}>{storePhone}</a></li>}
                {storeEmail && <li><a href={`mailto:${storeEmail}`}>{storeEmail}</a></li>}
                {storeAddress && <li>{storeAddress}</li>}
              </ul>
            </div>
            <div className="footer-section">
              <h3>{t('footer.shop', 'Shop')}</h3>
              <ul>
                {shopNav.map((item) => <li key={item.key}><Link to={item.url}>{item.label}</Link></li>)}
              </ul>
            </div>
            <div className="footer-section">
              <h3>{t('footer.quickLinks.title', 'Help')}</h3>
              <ul>
                {quickLinks.map((link, idx) => (
                  <li key={idx}>
                    {menuLink(link)}
                    {(link.children || []).filter((child) => child.enabled !== false).length > 0 && <ul className="footer-submenu">{link.children.filter((child) => child.enabled !== false).map((child, childIndex) => <li key={child.id || childIndex}>{menuLink(child)}</li>)}</ul>}
                  </li>
                ))}
                <li>
                  <Link to={isCustomerAuth ? '/account' : '/login'}>
                    {isCustomerAuth ? t('nav.account', 'My account') : t('nav.login', 'Log in')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>{t('footer.copyright', `© ${new Date().getFullYear()} ${storeName}`, { storeName, year: new Date().getFullYear() })}</p>
            {storeLegalName && storeLegalName !== storeName && (
              <p>{t('footer.brandOf', `${storeName} is a brand of ${storeLegalName}`, { storeName, company: storeLegalName })}</p>
            )}
            {storeNipt && <p>{t('footer.nipt', `NIPT ${storeNipt}`, { nipt: storeNipt })}</p>}
            <p className="footer-legal">
              <Link to="/cookies">{t('footer.cookies', 'Cookie policy')}</Link>
              {marketingConsentRequired && (
                <button type="button" onClick={resetConsent}>{t('footer.cookieSettings', 'Cookie settings')}</button>
              )}
            </p>
          </div>
        </div>
      </footer>

      <button
        className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label={t('nav.backToTop', 'Back to top')}
        tabIndex={showBackToTop ? 0 : -1}
      >
        <Icon.ScrollUp size={18} strokeWidth={1.75} aria-hidden="true" />
      </button>

      <WhatsAppButton />
      <ConsentBanner />
    </div>
  );
};

export default Layout;
